import { useState } from 'react';
import { ChevronLeft, PlusIcon } from 'lucide-react';

import SelectQuantity from './components/selectOffering/SelectQuantity';

import { currencyFormat } from '@/helpers/stringHelpers';
import {
  formatLocalDateWithDayOfWeek,
  convertTo12HourFormat,
  convertTo24HourFormat,
  extractDateFromISODateTimeString
} from '@/helpers/dateHelpers';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import SelectTime from '@/components/form/SelectTime';
import Spinner from '@/components/common/Spinner';
import Error from '@/components/common/Error';

import { CART_ITEM_TYPE } from '@/app/cartItem/enums';

import { useAddItemToCart } from '@/data/cart';
import { useCoachSelf } from '@/data/coach';
import { useOfferings } from '@/data/offering';

const SelectOffering = ({ cartId, onSuccess }) => {
  const coach = useCoachSelf();
  const offerings = useOfferings({ coachId: coach.data.id });
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [offeringId, setOfferingId] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const selectedOffering = offerings.data.content[offeringId];
  const addItemToCart = useAddItemToCart();

  const handleSetTime = (time) => {
    setTime(time);
  };

  const handleSelectTime = () => {
    setSelectedTime(convertTo24HourFormat(time));
  };

  const handleAddToCart = async () => {
    try {
      setSubmitting(true);
      const payload = {
        offeringId,
        type: CART_ITEM_TYPE.OFFERING,
        date: extractDateFromISODateTimeString(date),
        time: selectedTime,
        quantity
      };
      const newCartItem = await addItemToCart(cartId, payload);
      await onSuccess(newCartItem);
    } catch (err) {
      setApiError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!offeringId) {
    return (
      <>
        <h3>Select Offering</h3>
        <div className="space-y-2">
          {offerings.data.query.map((offeringId) => {
            const offering = offerings.data.content[offeringId];
            return (
              <Card key={offeringId} onClick={() => setOfferingId(offeringId)}>
                <CardContent className="p-2 flex items-center justify-between">
                  <div>
                    <h3>{offering.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {offering.activity.title}
                    </p>
                  </div>
                  <div>
                    <p>{currencyFormat(offering.pricePerClient)}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </>
    );
  }

  if (!date) {
    return (
      <div>
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOfferingId(null)}
            className="px-2"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Offerings
          </Button>
          <h3>{selectedOffering.name}</h3>
        </div>
        <h2>Select Date</h2>
        <div className="flex justify-center">
          <div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => {
                setDate(d ? d.toISOString() : d);
              }}
            />
          </div>
        </div>
      </div>
    );
  }

  if (!selectedTime) {
    return (
      <div>
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDate(null)}
            className="px-2"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Select Date
          </Button>
          <h3>{selectedOffering.name}</h3>
        </div>
        <div className="flex gap-4 mb-4">
          <span className="text-muted-foreground">Date:</span>
          <h3>{formatLocalDateWithDayOfWeek(date)}</h3>
        </div>

        <h3 className="mb-2">Select Time</h3>
        <div className="flex justify-between">
          <SelectTime onChange={handleSetTime} />
          <Button
            variant="default"
            size="icon"
            onClick={handleSelectTime}
            type="button"
            disabled={!time}
          >
            <PlusIcon className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setSelectedTime(null)}
          className="px-2"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Select Time
        </Button>
        <h3>{selectedOffering.name}</h3>
      </div>
      <div className="flex gap-4">
        <span className="text-muted-foreground">Date:</span>
        <h3>{formatLocalDateWithDayOfWeek(date)}</h3>
      </div>

      <div className="flex gap-4 mb-4">
        <span className="text-muted-foreground">Time:</span>
        <h3>{convertTo12HourFormat(selectedTime)}</h3>
      </div>
      <div className="flex gap-2 items-center mb-8">
        <span className="text-muted-foreground">Quantity:</span>
        <SelectQuantity quantity={quantity} setQuantity={setQuantity} />
      </div>
      {apiError && <Error error={apiError} />}
      <Button
        className="w-full"
        onClick={handleAddToCart}
        disabled={submitting}
      >
        {submitting && <Spinner className="w-5 h-5 mr-2" />}
        Add to Cart
      </Button>
    </div>
  );
};

export default SelectOffering;
